import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSessionUser } from '../../services/userService';
import '../../cms.css';
import { useSeo } from '../../hooks/useSeo';
import { UserProfile } from '../../types';
import { Loader2, ArrowLeft, Save, Check, Trash2 } from 'lucide-react';

const HoroscopeEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // State for dynamic dropdowns
    const [availableSigns, setAvailableSigns] = useState<string[]>([]);
    const [availableVerticals, setAvailableVerticals] = useState<string[]>([]);
    const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);

    const adminPath = location.pathname.split('/')[1];

    useSeo({ title: `CMS - ${id ? `Edit Horoscope #${id}` : 'New Horoscope'} - esoteric.pl` });

    const [formState, setFormState] = useState({
        sign: '',
        vertical: '',
        period: '',
        period_start: '',
        period_end: '',
        text: '',
        published_at: null as string | null
    });

    useEffect(() => {
        const sessionUser = getSessionUser();
        if (!sessionUser || sessionUser.rank !== 0) {
            navigate('/404');
            return;
        }
        setUser(sessionUser);

        const loadData = async () => {
            if (id) {
                try {
                    const res = await fetch(`/api/cms/horoscope?id=${id}`);
                    if (res.ok) {
                        const data = await res.json();
                        setFormState({
                            ...data,
                            published_at: data.published_at ? new Date(data.published_at).toISOString().slice(0, 16) : null
                        });
                    }
                } catch(e) {
                    console.error("Failed to load horoscope data", e);
                }
            }
            setLoading(false);
        };
        
        const fetchDropdownOptions = async () => {
            try {
                const [signsRes, verticalsRes, periodsRes] = await Promise.all([
                    fetch('/api/cms/horoscope?distinct=sign'),
                    fetch('/api/cms/horoscope?distinct=vertical'),
                    fetch('/api/cms/horoscope?distinct=period')
                ]);

                if (signsRes.ok) {
                    const signs: string[] = await signsRes.json();
                    const filteredSigns = signs.filter(s => s !== '[BLANK]');
                    setAvailableSigns(filteredSigns);
                    if (!id) {
                        setFormState(prev => ({...prev, sign: filteredSigns[0] || ''}));
                    }
                }

                if (verticalsRes.ok) {
                    const verticals: string[] = await verticalsRes.json();
                    const filteredVerticals = verticals.filter(v => v !== '[BLANK]');
                    setAvailableVerticals(filteredVerticals);
                    if (!id) { // Set default for new entries
                        setFormState(prev => ({...prev, vertical: filteredVerticals[0] || ''}));
                    }
                }
                if (periodsRes.ok) {
                    const periods: string[] = await periodsRes.json();
                    const filteredPeriods = periods.filter(p => p !== '[BLANK]');
                    setAvailablePeriods(filteredPeriods);
                     if (!id) { // Set default for new entries
                        setFormState(prev => ({...prev, period: filteredPeriods[0] || ''}));
                    }
                }
            } catch (e) {
                console.error("Failed to fetch dropdown options", e);
            }
        };

        fetchDropdownOptions();
        loadData();
    }, [id, navigate]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const isChecked = (e.target as HTMLInputElement).checked;
            setFormState(prev => ({ ...prev, [name]: isChecked ? new Date().toISOString().slice(0, 16) : null }));
        } else {
            setFormState(prev => ({ ...prev, [name]: value }));
        }
    };
    
    const handleSave = async () => {
        setSaving(true);
        setIsSuccess(false);
        
        const payload = {
            ...formState,
            published_at: formState.published_at ? new Date(formState.published_at).toISOString() : null
        };
        
        const res = await fetch(`/api/cms/horoscope`, {
            method: id ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(id ? { ...payload, id: parseInt(id) } : payload)
        });

        if (res.ok) {
            setIsSuccess(true);
            setTimeout(() => setIsSuccess(false), 2000);
            if (!id) {
                const data = await res.json();
                navigate(`/${adminPath}/horoscope/edit/${data.id}`, { replace: true });
            }
        } else {
            alert('Save failed.');
        }
        setSaving(false);
    };

    const handleDelete = async () => {
        if (!id || !confirm('Delete this entry?')) return;
        setSaving(true);
        try {
            await fetch('/api/cms/horoscope', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: [parseInt(id)] })
            });
            navigate(`/${adminPath}/horoscope`);
        } catch(e) { console.error(e); }
        setSaving(false);
    };

    if (loading) return <div className="min-h-screen bg-mystic-bg flex items-center justify-center"><Loader2 className="animate-spin text-white"/></div>;

    return (
        <div className="min-h-screen bg-mystic-bg text-[#e3e3e3] font-mono flex flex-col">
            <header className="flex-none h-16 border-b border-gray-800 bg-mystic-box px-6 flex items-center justify-between z-40">
                 <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/${adminPath}/horoscope`)} className="p-2 hover:bg-gray-800 rounded-full">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-bold text-white uppercase tracking-wider">
                        {id ? `Edit Horoscope #${id}` : 'New Horoscope'}
                    </h1>
                </div>
                 <div className="flex items-center gap-3">
                    {id && <button onClick={handleDelete} disabled={saving} className="text-red-400 hover:text-red-300 p-2"><Trash2 className="w-4 h-4"/></button>}
                    <button onClick={handleSave} disabled={saving} className={`flex items-center gap-2 font-bold px-6 py-2 rounded transition-all disabled:opacity-50 ${isSuccess ? 'bg-green-600 text-white' : 'bg-mystic-accent hover:bg-mystic-accentHover text-black'}`}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                        {isSuccess ? 'Saved' : 'Save'}
                    </button>
                </div>
            </header>
            <main className="flex-1 overflow-y-auto p-6">
                 <div className="max-w-4xl mx-auto space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                        <select name="sign" value={formState.sign} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white capitalize">
                            {availableSigns.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                         <select name="vertical" value={formState.vertical} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white capitalize">
                            {availableVerticals.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                         <select name="period" value={formState.period} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white capitalize">
                            {availablePeriods.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                     <div className="grid grid-cols-2 gap-4">
                        <input type="date" name="period_start" value={formState.period_start} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white"/>
                        <input type="date" name="period_end" value={formState.period_end} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white"/>
                    </div>
                    <textarea name="text" value={formState.text} onChange={handleInputChange} placeholder="Horoscope content (HTML allowed)" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white h-64"></textarea>
                    
                    <div className="flex items-center gap-4 border-t border-gray-800 pt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="published_at" checked={!!formState.published_at} onChange={handleInputChange} className="hidden" />
                            <div className={`w-4 h-4 border rounded flex items-center justify-center ${formState.published_at ? 'bg-mystic-accent border-mystic-accent' : 'border-gray-600'}`}>
                                {formState.published_at && <Check className="w-3 h-3 text-black"/>}
                            </div>
                            <span className="text-sm">Published</span>
                        </label>
                        {formState.published_at && (
                             <input type="datetime-local" value={formState.published_at} onChange={e => setFormState(p => ({...p, published_at: e.target.value}))} className="bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs"/>
                        )}
                    </div>
                 </div>
            </main>
        </div>
    );
};
export default HoroscopeEditor;